import React, { useState, useEffect } from 'react';
import { Box, Typography, Avatar, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import axiosInstance from '../axiosConfig';
import CoreCityGroupLogo from './CoreCityGroupLogo';

// Styled components for branding
const BrandLogo = styled(Avatar)(({ theme, size = 'medium' }) => ({
  width: size === 'small' ? 32 : size === 'medium' ? 48 : size === 'large' ? 64 : 48,
  height: size === 'small' ? 32 : size === 'medium' ? 48 : size === 'large' ? 64 : 48,
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  fontSize: size === 'small' ? '1rem' : size === 'medium' ? '1.5rem' : size === 'large' ? '2rem' : '1.5rem',
  fontWeight: 700,
  border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
  boxShadow: theme.shadows[4],
  transition: 'all 0.3s ease',
}));

const BrandName = styled(Typography)(({ theme, variant = 'h6' }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  transition: 'all 0.3s ease',
  color: theme.palette.mode === 'dark' ? '#ffffff' : '#333333',
  '& .gradient-text': {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
}));

const ProjectBadge = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  background: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 0, 0, 0.05)',
  border: `1px solid ${theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.2)' 
    : 'rgba(0, 0, 0, 0.1)'}`,
  backdropFilter: 'blur(10px)',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginLeft: theme.spacing(1),
}));

// Branding context and hook
const BrandingContext = React.createContext();

export const useBranding = () => {
  const context = React.useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

// Branding provider component
export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState({
    companyName: 'HARVEST AI',
    logo: null,
    logoUrl: null,
    projectName: '',
    projectId: '',
    theme: {
      primaryColor: '#667eea',
      secondaryColor: '#764ba2',
      accentColor: '#f093fb',
    },
    loading: true,
  });

  const theme = useTheme();

  useEffect(() => {
    loadBranding();
  }, []);

  const loadBranding = async () => {
    try {
      const projectId = localStorage.getItem('projectId');
      
      // Por ahora, usar branding por defecto sin hacer llamadas al backend
      // TODO: Implementar endpoint de branding en el backend cuando sea necesario
      setBranding({
        companyName: 'HARVEST AI',
        logo: null,
        logoUrl: null,
        projectName: projectId ? `Project ${projectId}` : 'Default Project',
        projectId: projectId || '',
        theme: {
          primaryColor: '#667eea',
          secondaryColor: '#764ba2',
          accentColor: '#f093fb',
        },
        loading: false,
      });

      // Comentado temporalmente hasta que se implemente el endpoint
      /*
      if (!projectId) {
        setBranding(prev => ({ ...prev, loading: false }));
        return;
      }

      // Fetch project branding data
      const response = await axiosInstance.get(`/api/projects/${projectId}/branding`);
      const projectData = response.data;

      setBranding({
        companyName: projectData.companyName || 'Core City Group',
        logo: projectData.logo,
        logoUrl: projectData.logoUrl,
        projectName: projectData.projectName || `Project ${projectId}`,
        projectId: projectId,
        theme: {
          primaryColor: projectData.theme?.primaryColor || '#667eea',
          secondaryColor: projectData.theme?.secondaryColor || '#764ba2',
          accentColor: projectData.theme?.accentColor || '#f093fb',
        },
        loading: false,
      });
      */
    } catch (error) {
      console.log('Error loading branding, using default:', error.message);
      // Fallback to default branding without throwing error
      const projectId = localStorage.getItem('projectId');
      setBranding({
        companyName: 'HARVEST AI',
        logo: null,
        logoUrl: null,
        projectName: projectId ? `Project ${projectId}` : 'Default Project',
        projectId: projectId || '',
        theme: {
          primaryColor: '#667eea',
          secondaryColor: '#764ba2',
          accentColor: '#f093fb',
        },
        loading: false,
      });
    }
  };

  const updateBranding = async (newBranding) => {
    try {
      // Por ahora, solo actualizar el estado local sin hacer llamadas al backend
      // TODO: Implementar endpoint de branding en el backend cuando sea necesario
      setBranding(prev => ({ ...prev, ...newBranding }));
      
      console.log('Branding updated locally (backend endpoint not implemented yet)');
      
      // Comentado temporalmente hasta que se implemente el endpoint
      /*
      const projectId = localStorage.getItem('projectId');
      if (!projectId) return;

      const formData = new FormData();
      if (newBranding.logo) {
        formData.append('logo', newBranding.logo);
      }
      formData.append('companyName', newBranding.companyName);
      formData.append('projectName', newBranding.projectName);
      formData.append('theme', JSON.stringify(newBranding.theme));

      await axiosInstance.post(`/api/projects/${projectId}/branding`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setBranding(prev => ({ ...prev, ...newBranding }));
      */
    } catch (error) {
      console.error('Error updating branding:', error);
      // No lanzar error para evitar que rompa la UI
      console.log('Branding update failed, but continuing with local state');
    }
  };

  const value = {
    branding,
    updateBranding,
    loadBranding,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

// Branding components
export const BrandLogoComponent = ({ size = 'medium', showName = false, showProject = false }) => {
  const { branding } = useBranding();
  const theme = useTheme();

  if (branding.loading) {
    return <BrandLogo size={size}>...</BrandLogo>;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {branding.logoUrl ? (
        <BrandLogo
          size={size}
          src={branding.logoUrl}
          alt={branding.companyName}
        />
      ) : (
        <CoreCityGroupLogo 
          size={size === 'small' ? 32 : size === 'medium' ? 48 : size === 'large' ? 64 : 48} 
          showText={false}
        />
      )}
      
      {showName && (
        <Box>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700,
              color: theme.palette.mode === 'dark' ? '#ffffff' : '#333333',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {branding.companyName}
          </Typography>
          {showProject && branding.projectName && (
            <ProjectBadge>
              {branding.projectName}
            </ProjectBadge>
          )}
        </Box>
      )}
    </Box>
  );
};

export const BrandHeader = ({ title, subtitle }) => {
  const { branding } = useBranding();
  const theme = useTheme();

  return (
    <Box sx={{ textAlign: 'center', mb: 4 }}>
      <BrandLogoComponent size="large" showName={true} showProject={true} />
      {title && (
        <Typography 
          variant="h4" 
          sx={{ 
            mt: 2, 
            fontWeight: 700,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography 
          variant="subtitle1" 
          sx={{ 
            mt: 1, 
            color: theme.palette.text.secondary,
            fontWeight: 500,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

export const BrandFooter = () => {
  const { branding } = useBranding();
  const theme = useTheme();

  return (
    <Box 
      sx={{ 
        textAlign: 'center', 
        py: 3, 
        mt: 'auto',
        borderTop: `1px solid ${theme.palette.divider}`,
        background: theme.palette.mode === 'dark' 
          ? 'rgba(0, 0, 0, 0.2)' 
          : 'rgba(0, 0, 0, 0.02)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <BrandLogoComponent size="small" showName={true} />
      <Typography 
        variant="caption" 
        sx={{ 
          display: 'block', 
          mt: 1, 
          color: theme.palette.text.secondary,
        }}
      >
        © {new Date().getFullYear()} {branding.companyName}. All rights reserved.
      </Typography>
    </Box>
  );
}; 